import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Building,
  MapPin,
  Target,
  TrendingUp,
  UserPlus
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { leadGenerationService } from '../../services/leadGenerationService'
import { formatRupeeAmount } from '../../utils/currencyFormat'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const LeadManagement = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    qualifiedLeads: 0,
    convertedLeads: 0,
    totalValue: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Define columns for the data table
  const columns = [
    {
      key: 'leadCode',
      label: 'Lead Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <UserPlus size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'prospect',
      label: 'Prospect Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value?.prospectName || 'N/A'}</div>
          {row.industrySegment && (
            <div className="text-sm text-gray-500">{row.industrySegment}</div>
          )}
        </div>
      )
    },
    {
      key: 'leadSource',
      label: 'Lead Source',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value?.leadSourceName || 'N/A'}
        </span>
      )
    },
    {
      key: 'industrySegment',
      label: 'Industry',
      sortable: true,
      render: (value) => (
        <div className="text-gray-900">{value || 'N/A'}</div>
      )
    },
    {
      key: 'interestArea',
      label: 'Interest',
      sortable: true,
      render: (value) => {
        const interestColors = {
          'ERP': 'bg-blue-100 text-blue-800',
          'CRM': 'bg-green-100 text-green-800',
          'HRMS': 'bg-purple-100 text-purple-800',
          'Accounting': 'bg-yellow-100 text-yellow-800',
          'Inventory': 'bg-orange-100 text-orange-800',
          'Project Management': 'bg-red-100 text-red-800',
          'Other': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${interestColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'leadStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'New': 'bg-gray-100 text-gray-800',
          'Qualified': 'bg-yellow-100 text-yellow-800',
          'Contacted': 'bg-blue-100 text-blue-800',
          'Demo Scheduled': 'bg-orange-100 text-orange-800',
          'Proposal Sent': 'bg-purple-100 text-purple-800',
          'Negotiation': 'bg-red-100 text-red-800',
          'Converted': 'bg-green-100 text-green-800',
          'Lost': 'bg-red-100 text-red-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value) => {
        const priorityColors = {
          'High': 'bg-red-100 text-red-800',
          'Medium': 'bg-yellow-100 text-yellow-800',
          'Low': 'bg-green-100 text-green-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'leadDate',
      label: 'Date',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    }
  ]

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData()
      hasLoadedRef.current = true
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [leadsResponse, statsResponse] = await Promise.all([
        leadGenerationService.getAllLeads(),
        leadGenerationService.getLeadStats()
      ])

      if (leadsResponse.success) {
        setData(leadsResponse.data.leadGenerations || [])
        
        if (leadsResponse.data.leadGenerations.length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No leads found. Create your first lead to get started.')
          hasShownEmptyToastRef.current = true
        }
      } else {
        toast.error(leadsResponse.message || 'Failed to load leads')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load lead data')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    navigate('/crm/lead-management/new')
  }

  const handleEdit = (row) => {
    navigate('/crm/lead-management/edit', { 
      state: { leadData: row } 
    })
  }

  const handleView = (row) => {
    navigate('/crm/lead-management/view', { 
      state: { leadData: row } 
    })
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await leadGenerationService.deleteLead(deleteItem._id)
      if (response.success) {
        toast.success('Lead deleted successfully')
        loadData()
        setShowDeleteModal(false)
        setDeleteItem(null)
      } else {
        toast.error(response.message || 'Failed to delete lead')
      }
    } catch (error) {
      console.error('Error deleting lead:', error)
      toast.error('Failed to delete lead')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select leads to delete')
      return
    }

    try {
      const response = await leadGenerationService.bulkDeleteLeads(selectedRows)
      if (response.success) {
        toast.success(response.message)
        setSelectedRows([])
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete leads')
      }
    } catch (error) {
      console.error('Error bulk deleting leads:', error)
      toast.error('Failed to delete leads')
    }
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Transactions',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Lead Management',
      path: '/crm/lead-management',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Lead Management</h1>
          <p>Manage and track all leads and prospects</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAdd}
          >
            <Plus size={16} />
            Add Lead
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalLeads || 0}</div>
          <div className="metric-title">Total Leads</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.newLeads || 0}</div>
          <div className="metric-title">New Leads</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.qualifiedLeads || 0}</div>
          <div className="metric-title">Qualified</div>
          <div className="metric-subtitle">Lead progression</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+20%</span>
            </div>
          </div>
          <div className="metric-value">{formatRupeeAmount(stats.totalValue || 0)}</div>
          <div className="metric-title">Total Value</div>
          <div className="metric-subtitle">Pipeline value</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search leads..."
          emptyMessage="No leads found. Create your first lead to get started."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Lead"
        message={`Are you sure you want to delete lead "${deleteItem?.leadCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default LeadManagement
